import { useState } from 'react';

import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { geocodeAddress } from '../utils/geocoding';

export default function CapabilityLocationFields({
  locationAddress = '',
  latitude = '',
  longitude = '',
  onChange,
  disabled = false,
}) {
  const [lookupStatus, setLookupStatus] = useState({ loading: false, message: '', severity: 'info' });

  const updateLocation = (patch) => {
    setLookupStatus({ loading: false, message: '', severity: 'info' });
    onChange(patch);
  };

  const handleAddressChange = (event) => {
    updateLocation({
      location_address: event.target.value,
      latitude: '',
      longitude: '',
    });
  };

  const handleLookup = async () => {
    setLookupStatus({ loading: true, message: '', severity: 'info' });
    try {
      const result = await geocodeAddress(locationAddress);
      onChange({
        latitude: result.latitude.toFixed(6),
        longitude: result.longitude.toFixed(6),
      });
      setLookupStatus({
        loading: false,
        message: `Coordinates found for ${result.displayName}.`,
        severity: 'success',
      });
    } catch (error) {
      setLookupStatus({
        loading: false,
        message: error?.message || 'Unable to find coordinates. Enter them manually.',
        severity: 'warning',
      });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Capability Location
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter both coordinates directly, or enter an address and find its coordinates. The address can still be saved if lookup fails.
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <TextField
            label="Address"
            value={locationAddress}
            onChange={handleAddressChange}
            disabled={disabled}
            placeholder="Street, city, state, postal code, country"
            fullWidth
          />
          <Button
            variant="outlined"
            startIcon={<MyLocationRoundedIcon />}
            onClick={handleLookup}
            disabled={disabled || lookupStatus.loading || !String(locationAddress).trim()}
            sx={{ minWidth: 190, minHeight: 56 }}
          >
            {lookupStatus.loading ? 'Finding...' : 'Find Coordinates'}
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Latitude"
            type="number"
            value={latitude}
            onChange={(event) => updateLocation({ latitude: event.target.value })}
            disabled={disabled}
            inputProps={{ min: -90, max: 90, step: 'any' }}
            helperText="-90 to 90"
            fullWidth
          />
          <TextField
            label="Longitude"
            type="number"
            value={longitude}
            onChange={(event) => updateLocation({ longitude: event.target.value })}
            disabled={disabled}
            inputProps={{ min: -180, max: 180, step: 'any' }}
            helperText="-180 to 180"
            fullWidth
          />
        </Stack>
        {lookupStatus.message ? <Alert severity={lookupStatus.severity}>{lookupStatus.message}</Alert> : null}
        <Typography variant="caption" color="text.secondary">
          Finding coordinates sends the address to the configured geocoding service. The default service uses OpenStreetMap Nominatim data © OpenStreetMap contributors.
        </Typography>
      </Stack>
    </Paper>
  );
}
